import { useAuth } from "@/auth/hooks/useAuth";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import * as React from "react";

const historicalRateToEuroCache = new Map<string, Promise<number | null>>();

export type RealFundingSummary = {
  financier?: { id?: number | string | null } | null;
  financer?: { id?: number | string | null } | null;
  verdeo?: boolean | null;
  verdeoFundingInvoiced?: unknown;
  verdeoFundingPaid?: unknown;
  paidAmount?: number | string | null;
  paidAmountConverted?: number | string | null;
  paid_amount_converted?: number | string | null;
  paidDevise?: { id?: number | string | null; code?: string | null } | number | string | null;
  paid_devise?: { id?: number | string | null; code?: string | null } | number | string | null;
  devise?: { id?: number | string | null; code?: string | null } | number | string | null;
  obtainedFundings?: unknown;
  obtainedFunding?: unknown;
  obtained_fundings?: unknown;
  obtained_funding?: unknown;
  dateAmount?: string | Date | null;
};

type ProjectFundingSummary = {
  country?: { currency?: { code?: string | null } };
  realFundings?: RealFundingSummary[];
};

type FundingByProjectPayload = {
  data?: ProjectFundingSummary[];
};

function normalizeDateOnly(input: unknown): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

function readFinancierIdFromUnknown(value: unknown): number | null {
  if (!value) return null;
  if (typeof value === "object") {
    const id = Number((value as any)?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

async function fetchHexaRateToEuroAtDate(
  currencyCode: string,
  dateInput: unknown
): Promise<number | null> {
  const code = String(currencyCode ?? "")
    .trim()
    .toUpperCase();
  const day = normalizeDateOnly(dateInput);
  if (!code || !day) return null;
  if (code === "EUR") return 1;

  const cacheKey = `${code}@${day}`;
  const cached = historicalRateToEuroCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const response = await fetch(
        `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${code}&date=${day}`
      );
      if (!response.ok) return null;
      const payload = await response.json();
      const entries = Array.isArray(payload) ? payload : [];
      const entry = (entries as any[]).find((e) => e.quote === code);
      const rate = Number(entry?.rate);
      if (!Number.isFinite(rate) || rate <= 0) return null;
      const rateToEuro = 1 / rate;
      return Number.isFinite(rateToEuro) && rateToEuro > 0 ? rateToEuro : null;
    } catch {
      return null;
    }
  })();

  historicalRateToEuroCache.set(cacheKey, promise);
  return promise;
}

function readNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeCollectionLike(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const values = Object.values(value as Record<string, unknown>);
  if (!values.length) return [];
  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object"
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function readCodeFromCurrencyLike(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    const code = raw.toUpperCase();
    return code || null;
  }
  if (typeof value === "object") {
    const raw = String((value as any)?.code ?? (value as any)?.currencyCode ?? "")
      .trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    const code = raw.toUpperCase();
    return code || null;
  }
  return null;
}

function readIdFromCurrencyLike(value: unknown): number | null {
  const raw =
    typeof value === "object"
      ? (value as any)?.id
      : typeof value === "number" || typeof value === "string"
      ? value
      : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(
  funding: RealFundingSummary,
  contractCurrencyCode?: string
): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const normalizedContractCode = String(contractCurrencyCode ?? "")
    .trim()
    .toUpperCase();
  if (directId && normalizedContractCode) {
    return normalizedContractCode;
  }

  return null;
}

export function readFundingFinancierId(
  funding: RealFundingSummary
): number | null {
  const directId =
    readFinancierIdFromUnknown((funding as any)?.financier) ??
    readFinancierIdFromUnknown((funding as any)?.financer) ??
    null;
  if (directId) return directId;

  const obtainedRows = normalizeCollectionLike(
    (funding as any)?.obtainedFundings ??
      (funding as any)?.obtainedFunding ??
      (funding as any)?.obtained_fundings ??
      (funding as any)?.obtained_funding
  );
  for (const row of obtainedRows) {
    const id =
      readFinancierIdFromUnknown(row?.financer) ??
      readFinancierIdFromUnknown(row?.financier) ??
      null;
    if (id) return id;
  }

  return null;
}

function isNonVerdeoFundingContract(funding: RealFundingSummary): boolean {
  if (funding.verdeo === false) return true;
  if (funding.verdeo === true) return false;
  if (
    funding.verdeoFundingInvoiced != null ||
    funding.verdeoFundingPaid != null
  ) {
    return false;
  }
  return true;
}

export async function resolveFundingEuroAmount(
  funding: RealFundingSummary,
  contractCurrencyCode: string
): Promise<number> {
  const storedEuro = readNumber(funding?.paidAmount) ?? 0;
  if (!isNonVerdeoFundingContract(funding)) return storedEuro;

  const inputCurrencyCode = readFundingInputCurrencyCode(
    funding,
    contractCurrencyCode
  );
  const inputIsLocalCurrency = !!inputCurrencyCode && inputCurrencyCode !== "EUR";
  if (!inputIsLocalCurrency) return storedEuro;

  const converted =
    readNumber(funding?.paidAmountConverted) ??
    readNumber(funding?.paid_amount_converted) ??
    null;
  if (converted == null || converted <= 0) return storedEuro;

  const rateCurrencyCode = String(
    inputCurrencyCode && inputCurrencyCode !== "EUR"
      ? inputCurrencyCode
      : contractCurrencyCode
  )
    .trim()
    .toUpperCase();

  const rateToEuro = await fetchHexaRateToEuroAtDate(
    rateCurrencyCode,
    funding?.dateAmount
  );
  if (rateToEuro != null && rateToEuro > 0) {
    return converted * rateToEuro;
  }

  return storedEuro;
}

export function useFunding() {
  const { user } = useAuth();

  const {
    data: projectData,
  } = useFetchDataV2<FundingByProjectPayload>(
    `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project`,
    `projectFundingHeader`,
    1000 * 60 * 60
  );

  const [funding, setFunding] = React.useState<number>(0);

  React.useEffect(() => {
    const fetchFunding = async () => {
      let fundingTemp = 0;
      const projects = Array.isArray(projectData?.data) ? projectData.data : [];

      for (const project of projects) {
        const contractCurrencyCode = String(
          project?.country?.currency?.code ?? ""
        );
        const fundings = Array.isArray(project?.realFundings)
          ? project.realFundings
          : [];

        for (const rf of fundings) {
          const financierId = readFundingFinancierId(rf);
          if (Number(financierId) !== Number(user?.id)) continue;
          fundingTemp += await resolveFundingEuroAmount(
            rf,
            contractCurrencyCode
          );
        }
      }

      setFunding(fundingTemp);
    };

    if (user && projectData) {
      fetchFunding();
    }
  }, [projectData, user]);

  return funding;
}
